import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { KeyIcon, PencilIcon } from "@heroicons/react/24/outline";

import UserAvatar from "@components/layout/zenith/UserAvatar";
import Alert from "@components/shared/Alert";
import { useAppContext } from "@context/AppContext";
import DetailList from "./DetailList";
import RequestUpdateDialog from "./RequestUpdateDialog";
import ChangePasswordDialog from "./ChangePasswordDialog";
import ExportMenu from "./ExportMenu";
import { DetailRows, exportDetails } from "./exportDetails";
import { smallButton } from "./smallButton";

const MyDetails: FunctionComponent = () => {
  const { t } = useTranslation();
  const {
    loggedInInfo: { userDetails },
  } = useAppContext();
  const [dialog, setDialog] = useState<"request" | "password">();
  const [notice, setNotice] = useState<string>();
  const rows: DetailRows = [
    [t("user.username"), userDetails?.name],
    [t("user.email"), userDetails?.email],
    [t("user.tel"), userDetails?.tel],
    [t("subscriber.account_number"), userDetails?.subscriber?.accountNumber],
    [
      t("user.holder_type"),
      userDetails?.isPrimary ? t("user.primary") : t("user.general"),
    ],
  ];
  const closeWith = (message: string) => () => {
    setDialog(undefined);
    setNotice(message);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {notice ? (
        <Alert type="success" className="lg:col-span-2">
          {notice}
        </Alert>
      ) : null}
      <div>
        <div className="mb-6 flex items-center gap-4">
          <UserAvatar name={userDetails?.name} className="h-14 w-14 text-xl" />
          <div className="min-w-0">
            <h2 className="text-base font-bold text-primary-3">
              {userDetails?.name}
            </h2>
            <p className="break-all text-sm text-gray-500">
              {userDetails?.email}
            </p>
            {userDetails?.isPrimary ? (
              <span className="mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-primary-1">
                {t("user.primary_user")}
              </span>
            ) : null}
          </div>
        </div>
        <DetailList rows={rows} />
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className={`btn-outline-primary ${smallButton}`}
            onClick={() => setDialog("request")}
          >
            <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t("profile.request_update")}
          </button>
          <ExportMenu
            onExport={format =>
              exportDetails({
                title: userDetails?.name,
                rows,
                fileName: userDetails?.email,
                format,
              })
            }
          />
        </div>
      </div>
      <div>
        <h3 className="mb-3 text-sm font-bold text-primary-3">
          {t("zenith.profile.security")}
        </h3>
        <button
          type="button"
          className={`btn-default ${smallButton}`}
          onClick={() => setDialog("password")}
        >
          <KeyIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {t("auth.change_password")}
        </button>
      </div>
      {dialog === "request" ? (
        <RequestUpdateDialog
          onClose={() => setDialog(undefined)}
          onSent={closeWith(t("profile.request_update_success"))}
        />
      ) : null}
      {dialog === "password" ? (
        <ChangePasswordDialog
          onClose={() => setDialog(undefined)}
          onChanged={closeWith(t("auth.reset_password_success_message"))}
        />
      ) : null}
    </div>
  );
};

export default MyDetails;
